import React, { useMemo, useEffect } from "react";
import { Col, Container, Form, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { classifySupport, SupportAxis } from "@common/helpers/sharedTypes";
import { ng } from "@common/ngds";
import {
  useProject,
  fetchAutoBuckling,
  getStiffBuckling,
  getWeakBuckling,
  selectBucklingStatus,
  type BucklingRow,
} from "../features/projects/projectSlice";
import { useUnit } from "../features/unit/unitSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { Alert } from "react-bootstrap";
import { shallowEqual } from "react-redux";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import type { NGDropdownListItem } from "@common/components/ui-components/SXDropdown";

const LOAD_POSITIONS: NGDropdownListItem<ng.beam.Alignment>[] = [
  { id: ng.beam.Alignment.ALIGNMENT_TOP, name: "top_edge_.label" },
  { id: ng.beam.Alignment.ALIGNMENT_CENTER, name: "center_of_gravity_.label" },
  { id: ng.beam.Alignment.ALIGNMENT_BOTTOM, name: "bottom_edge_.label" },
];

const SUPPORT_CONDITIONS: NGDropdownListItem<ng.beam.SupportCondition>[] = [
  { id: ng.beam.SupportCondition.SUPPORT_CONDITION_CANTILEVER, name: "cantilever_.label" },
  { id: ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY, name: "simply_supported_.label" },
];

const deriveLtbRows = (fb: ng.beam.LTBuckling | null): BucklingRow[] => {
  if (!fb || fb.data.length === 0) return [];
  return fb.data.map((d, i) => {
    const start = safeFinite(d.position?.start, 0);
    const end = safeFinite(d.position?.end, 0);
    const spanLength = Math.max(0, end - start);
    const rawBeta = fb.betas[i] ?? d.getBeta();
    const beta = safeFinite(rawBeta, 1.0);
    return {
      spanIndex: i + 1,
      start,
      end,
      spanLength,
      beta: beta > 0 ? beta : 1.0,
      lc: (beta > 0 ? beta : 1.0) * spanLength,
    };
  });
};

type TimberLtsRow = BucklingRow & { supportCondition: ng.beam.SupportCondition };

const deriveLtsRows = (ltsb: ng.beam.LTSBuckling | null): TimberLtsRow[] => {
  if (!ltsb || ltsb.data.length === 0) return [];
  return ltsb.data.map((d, i) => {
    const start = safeFinite(d.position?.start, 0);
    const end = safeFinite(d.position?.end, 0);
    const spanLength = Math.max(0, end - start);
    const beta = safeFinite(d.getBeta(), 1.0);
    const supportCondition =
      ltsb.supportConditions[i] ?? d.getSupportCondition() ?? ng.beam.SupportCondition.SUPPORT_CONDITION_SIMPLY;
    return {
      spanIndex: i + 1,
      start,
      end,
      spanLength,
      beta,
      lc: (beta > 0 ? beta : 1.0) * spanLength,
      supportCondition,
    };
  });
};

export interface SXBucklingProps {
  // No props yet
}

const safeFinite = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const deriveBucklingRows = (fb: ng.beam.FlexBuckling | null): BucklingRow[] => {
  if (!fb || fb.data.length === 0) return [];
  return fb.data.map((d, i) => {
    const start = safeFinite(d.position?.start, 0);
    const end = safeFinite(d.position?.end, 0);
    const spanLength = Math.max(0, end - start);
    const rawBeta = fb.betas[i] ?? d.getBeta();
    const beta = safeFinite(rawBeta, 1.0);
    return {
      spanIndex: i + 1,
      start,
      end,
      spanLength,
      beta: beta > 0 ? beta : 1.0,
      lc: (beta > 0 ? beta : 1.0) * spanLength,
    };
  });
};

export const SXBuckling: React.FC<SXBucklingProps> = () => {
  const { lengthUnit, toDisplayLength, fromDisplayLength } = useUnit();
  const {
    project: selProject,
    setAutoBuckling,
    updateBucklingBeta,
    updateBucklingLc,
    setSteelBucklingLoadPosition,
    updateSteelLtbBeta,
    updateSteelLtbLc,
    setSteelContinuouslyRestrained,
    setTimberBucklingLoadPosition,
    updateTimberLtsbLc,
    updateTimberLtsbSupportCondition,
    setTimberLtsbContinuouslyRestrained,
    getProjectSectionObj,
    secondOrder,
  } = useProject();
  const dispatch = useAppDispatch();
  const bucklingStatus = useAppSelector(selectBucklingStatus);
  const { t } = useTranslation();

  const section = getProjectSectionObj();
  const rawCategory = (section as any)?.materialCategory;
  const materialCategory =
    rawCategory != null
      ? ng.sections.MaterialCategory_FromObject(rawCategory)
      : ng.sections.MaterialCategory.MATERIAL_CATEGORY_UNSPECIFIED;

  const isAuto = (selProject?.data as any)?.settings?.endpointCache?.autoBucklingLength ?? true;
  const inputsDisabled = isAuto || bucklingStatus === "loading";

  const beam = useMemo(() => {
    if (!selProject) return null;
    const elementJson = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0] ?? null;
    if (!elementJson?.beam) return null;

    return ng.beam.Data.fromObject(elementJson.beam);
  }, [selProject]);

  const stiffRows = useMemo(() => deriveBucklingRows(beam ? getStiffBuckling(beam) : null), [beam]);
  const weakRows = useMemo(() => deriveBucklingRows(beam ? getWeakBuckling(beam) : null), [beam]);
  const steelLoadPosition = beam?.getSteel()?.loadPosition;
  const selectedLoadPosition = useMemo(
    () => LOAD_POSITIONS.find((item) => item.id === steelLoadPosition) ?? null,
    [steelLoadPosition],
  );
  const timberLoadPosition = beam?.getTimber()?.loadPosition;
  const selectedTimberLoadPosition = useMemo(
    () => LOAD_POSITIONS.find((item) => item.id === timberLoadPosition) ?? null,
    [timberLoadPosition],
  );

  const steelElement = beam?.getSteel() ?? null;
  const ltbTopRows = useMemo(() => deriveLtbRows(steelElement?.ltbTop ?? null), [beam]);
  const ltbBtmRows = useMemo(() => deriveLtbRows(steelElement?.ltbBtm ?? null), [beam]);
  const ltbTopContinuous = steelElement?.ltbTop?.continouslyRestrained ?? false;
  const ltbBtmContinuous = steelElement?.ltbBtm?.continouslyRestrained ?? false;

  const timberElement = beam?.getTimber() ?? null;
  const ltsRows = useMemo(() => deriveLtsRows(timberElement?.ltsb ?? null), [beam]);
  const ltsContinuous = timberElement?.ltsb?.continouslyRestrained ?? false;

  const supportClassifications = useMemo(() => {
    const sups = selProject?.data.data?.input?.structures[0]?.stages[0]?.sups ?? [];
    const nonLateralSups = sups.filter((sup: any) => !sup.lateral);
    const axes: SupportAxis[] = ["Y", "Z"];

    return nonLateralSups.map((supJson: any) => {
      const sup = ng.stage.Support.fromObject(supJson);
      return {
        guid: sup.guid,
        y: classifySupport(sup, axes[0]),
        z: classifySupport(sup, axes[1]),
      };
    });
  }, [selProject]);

  const bucklingTrigger = useAppSelector((state) => {
    const structure = state.ProjectSlice.project?.data?.data?.input?.structures?.[0];
    return {
      length: structure?.elements?.[0]?.beam?.segments?.[0]?.length,
      sups: structure?.stages?.[0]?.sups, // array ref — not touched by setPrimaryBeamData
      secGuid: structure?.elements?.[0]?.beam?.segments?.[0]?.layer?.start?.secGuid,
      mtrlGuid: structure?.elements?.[0]?.beam?.mtrlGuid,
      steelLoadPosition: structure?.elements?.[0]?.beam?.steel?.loadPosition,
      timberLoadPosition: structure?.elements?.[0]?.beam?.timber?.loadPosition,
    };
  }, shallowEqual);

  useEffect(() => {
    if (isAuto && selProject) {
      dispatch(fetchAutoBuckling({ file: selProject }));
    }
  }, [bucklingTrigger]);

  const renderTable = (axis: "Y" | "Z", rows: BucklingRow[], lcLabel: React.ReactNode, betaLabel: React.ReactNode) => (
    <Col>
      <Table hover size="sm" className="main-sx-table">
        <thead className="table-header-bg">
          <tr className="table-header align-items-center">
            <th>{t("span_.label")}</th>
            <th>
              {lcLabel} ({lengthUnit})
            </th>
            <th>{betaLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} style={{ textAlign: "center", color: "#999" }}>
                —
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={`${axis}-${row.spanIndex}`}>
              <td>
                <SXEditField
                  disabled
                  id={`sx-buckling-span-${axis}-${row.spanIndex}`}
                  tableView
                  type="number"
                  textAlign="left"
                  fontSize={12}
                  val={row.spanIndex}
                />
              </td>
              <td>
                <SXEditField
                  id={`sx-buckling-lc-${axis}-${row.spanIndex}`}
                  tableView
                  type="number"
                  textAlign="left"
                  fontSize={12}
                  val={toDisplayLength(row.lc)}
                  disabled={inputsDisabled}
                  onUpdate={(newLcDisplay) => {
                    const lc = fromDisplayLength(newLcDisplay as number);
                    updateBucklingLc(axis, row.spanIndex - 1, lc);
                  }}
                />
              </td>
              <td>
                <SXEditField
                  id={`sx-buckling-beta-${axis}-${row.spanIndex}`}
                  tableView
                  type="number"
                  textAlign="left"
                  fontSize={12}
                  val={Number(row.beta.toFixed(4))}
                  disabled={inputsDisabled}
                  onUpdate={(newBeta) => {
                    updateBucklingBeta(axis, row.spanIndex - 1, Number(newBeta));
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Col>
  );

  const renderLtbTable = (loc: "top" | "btm", rows: BucklingRow[], continuous: boolean, title: string) => (
    <Col>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <Table hover size="sm" className="main-sx-table">
        <thead className="table-header-bg">
          <tr className="table-header align-items-center">
            <th>{t("span_.label")}</th>
            <th>Lc ({lengthUnit})</th>
            <th>
              <span>&beta;</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} style={{ textAlign: "center", color: "#999" }}>
                —
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={`${loc}-ltb-${row.spanIndex}`}>
              <td style={{ fontSize: 12, paddingLeft: 8 }}>{row.spanIndex}</td>
              <td>
                <SXEditField
                  id={`sx-ltb-lc-${loc}-${row.spanIndex}`}
                  tableView
                  type="number"
                  textAlign="left"
                  fontSize={12}
                  val={toDisplayLength(row.lc)}
                  disabled={continuous}
                  disabledDisplay="-"
                  onUpdate={(val) => {
                    const lc = fromDisplayLength(val as number);
                    updateSteelLtbLc(loc, row.spanIndex - 1, lc);
                  }}
                />
              </td>
              <td>
                <SXEditField
                  id={`sx-ltb-beta-${loc}-${row.spanIndex}`}
                  tableView
                  type="number"
                  textAlign="left"
                  fontSize={12}
                  val={Number(row.beta.toFixed(4))}
                  disabled={continuous}
                  disabledDisplay="-"
                  onUpdate={(val) => {
                    updateSteelLtbBeta(loc, row.spanIndex - 1, Number(val));
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Form.Check
        type="switch"
        className="sx-switch"
        id={`sx-ltb-${loc}-continuous-switch`}
        label={t("continuously_restrained_.label")}
        checked={continuous}
        onChange={(e) => setSteelContinuouslyRestrained(loc, e.target.checked)}
        style={{ fontSize: 12, marginTop: 4 }}
      />
    </Col>
  );

  const renderTimberLtsTable = (rows: TimberLtsRow[], continuous: boolean) => (
    <Col>
      <Table hover size="sm" className="main-sx-table">
        <thead className="table-header-bg">
          <tr className="table-header align-items-center">
            <th>{t("span_.label")}</th>
            <th>Lc ({lengthUnit})</th>
            <th>{t("support_condition_.label")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} style={{ textAlign: "center", color: "#999" }}>
                —
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const selectedCondition = SUPPORT_CONDITIONS.find((item) => item.id === row.supportCondition) ?? null;
            return (
              <tr key={`timber-lts-${row.spanIndex}`}>
                <td>
                  <SXEditField
                    disabled
                    id={`sx-lts-span-${row.spanIndex}`}
                    tableView
                    type="number"
                    textAlign="left"
                    fontSize={12}
                    val={row.spanIndex}
                  />
                </td>
                <td>
                  <SXEditField
                    id={`sx-lts-lc-${row.spanIndex}`}
                    tableView
                    type="number"
                    textAlign="left"
                    fontSize={12}
                    val={toDisplayLength(row.lc)}
                    disabled={continuous}
                    disabledDisplay="-"
                    onUpdate={(val) => {
                      const lc = fromDisplayLength(val as number);
                      updateTimberLtsbLc(row.spanIndex - 1, lc);
                    }}
                  />
                </td>
                <td>
                  <SXDropdown
                    id={`sx-lts-support-condition-${row.spanIndex}`}
                    tableView
                    className="sx-dropdown"
                    name={`lts-support-condition-${row.spanIndex}`}
                    lang="en"
                    list={SUPPORT_CONDITIONS}
                    val={selectedCondition}
                    onUpdate={(value) => {
                      if (!value) {
                        return;
                      }
                      updateTimberLtsbSupportCondition(row.spanIndex - 1, value.id);
                    }}
                    disabled={continuous}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <Form.Check
        type="switch"
        className="sx-switch"
        id="sx-lts-continuous-switch"
        label={t("continuously_restrained_.label")}
        checked={continuous}
        onChange={(e) => setTimberLtsbContinuouslyRestrained(e.target.checked)}
        style={{ fontSize: 12, marginTop: 4 }}
      />
    </Col>
  );

  return (
    <>
      <Row className="align-items-center ps-0 pb-4 m-0" data-support-count={supportClassifications.length}>
        {/* A second-order analysis derives the buckling behaviour from the deformed geometry it
            solves, so the buckling lengths and their beta factors have nothing left to feed — the
            same "this section has nothing to set" note the fire settings show without a fire load
            combination. Lateral torsional buckling is not covered by the analysis and stays below.
            The values themselves are left untouched, so switching second order back off returns the
            spans exactly as they were. */}
        {secondOrder ? (
          <Container style={{ color: "#696969" }}>
            <Row className="p-2 border rounded mb-2" style={{ borderColor: "var(--panel-border)" }}>
              <Col>
                <div className="mt-2" style={{ color: "#696969" }}>
                  <span style={{ fontWeight: "bold" }}>
                    {t(
                      "second_order_makes_buckling_redundant_.label",
                      "When a second-order analysis is selected, buckling settings are redundant, since the buckling check is inherently included in the analysis.",
                    )}
                  </span>
                </div>
              </Col>
            </Row>
          </Container>
        ) : (
          <>
            <Col>
              <Container style={{ fontSize: 12 }}>
                <Row className="container-row" style={{ alignItems: "center" }}>
                  <Form.Check
                    type="switch"
                    className="sx-switch"
                    label={t("auto_buck_length_.label")}
                    id="sx-auto-buckling-length-switch"
                    checked={isAuto}
                    onChange={(e) => {
                      setAutoBuckling(e.target.checked);
                      if (e.target.checked && selProject) {
                        dispatch(fetchAutoBuckling({ file: selProject }));
                      }
                    }}
                  />
                </Row>
              </Container>
            </Col>

            {bucklingStatus === "failed" && (
              <Alert variant="warning" className="mt-2 mb-2 py-1 px-2" style={{ fontSize: 12 }}>
                {t("auto_buck_failed_.message")}
              </Alert>
            )}

            <Container className="sx-table-container">
              <Row>
                {renderTable(
                  "Y",
                  stiffRows,
                  <>
                    L<sub>cy</sub>
                  </>,
                  <span>
                    &beta;<sub>y</sub>
                  </span>,
                )}
                {renderTable(
                  "Z",
                  weakRows,
                  <>
                    L<sub>cz</sub>
                  </>,
                  <span>
                    &beta;<sub>z</sub>
                  </span>,
                )}
              </Row>
            </Container>
          </>
        )}

        {materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_STEEL && (
          <Container>
            <Row className="container-row pb-0" style={{ alignItems: "center", fontWeight: "bold" }}>
              <Col xs={12} className="editfield-label ps-0 pb-0">
                {t("lateral_torsional_buckling_.label")} ({t("weak_direction_.label")})
              </Col>
            </Row>
            <Row className="container-row mt-2">
              <Col className="editfield-label">{t("load_position_.label")}</Col>
              <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                <SXDropdown
                  id="sx-buckling-load_positions-dropdown"
                  toggleId="sx_buckling_load_positions_toggle"
                  className="sx-dropdown"
                  dataTestId="dropdown_buckling_load_position"
                  name="lpos"
                  lang="en"
                  list={LOAD_POSITIONS}
                  val={selectedLoadPosition}
                  onUpdate={(value) => {
                    if (!value) {
                      return;
                    }
                    setSteelBucklingLoadPosition(value.id);
                  }}
                  disabled={false}
                />
              </Col>
            </Row>
            <Container className="sx-table-container mt-2">
              <Row>
                {renderLtbTable("top", ltbTopRows, ltbTopContinuous, t("top_flange_.label"))}
                {renderLtbTable("btm", ltbBtmRows, ltbBtmContinuous, t("bottom_flange_.label"))}
              </Row>
            </Container>
          </Container>
        )}

        {materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_TIMBER && (
          <Container>
            <Row className="container-row" style={{ alignItems: "center", fontWeight: "bold" }}>
              <Col xs={12} className="editfield-label ps-0 pb-0">
                {t("lateral_torsional_and_shear_buckling_.label")}
              </Col>
            </Row>
            <Row className="container-row mt-2">
              <Col className="editfield-label">{t("load_position_.label")}</Col>
              <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                <SXDropdown
                  id="sx-timber-buckling-load_positions-dropdown"
                  toggleId="sx_timber_buckling_load_positions_toggle"
                  className="sx-dropdown"
                  dataTestId="dropdown_timber_buckling_load_position"
                  name="timber_lpos"
                  lang="en"
                  list={LOAD_POSITIONS}
                  val={selectedTimberLoadPosition}
                  onUpdate={(value) => {
                    if (!value) {
                      return;
                    }
                    setTimberBucklingLoadPosition(value.id);
                  }}
                  disabled={false}
                />
              </Col>
            </Row>
            <Container className="sx-table-container mt-2">
              <Row>{renderTimberLtsTable(ltsRows, ltsContinuous)}</Row>
            </Container>
          </Container>
        )}
      </Row>
    </>
  );
};
